/**
 * Платёж по СБП, который ещё не стал заказом, — как он читается в карточке на главной.
 *
 * Порядок ветвления — договорённость с сервером, не догадка: возврат денег важнее любого
 * статуса (человеку важно одно — деньги вернулись); отказ важнее «оплачен»; оформленный заказ
 * важнее «оформляем»; и только неоплаченный код делится на живой и истёкший — по `expires_at`,
 * с тем же запасом, что и у экрана ожидания, чтобы карточка и экран не разошлись во мнении о
 * том, ждём ли мы ещё банк.
 *
 * Цвет никогда не единственный сигнал: каждая плашка несёт слово (docs/22 §3.1).
 */
import { formatLiters, formatMoney } from '@/shared/lib/format';
import { SBP_EXPIRY_GRACE_MS, type SbpPendingPayment } from '@/entities/order/sbp';
import { historyDateTimeLabel } from '@/entities/order/model/history';
import type { StatusVariant } from './orderStatus';

export type SbpPendingKind =
  /** Код живой, оплаты ещё нет — можно открыть банк и заплатить. */
  | 'awaiting'
  /** Срок кода вышел, банк так и не ответил. Платить по нему уже нельзя. */
  | 'expired'
  /** Деньги получены, сервер оформляет заказ. */
  | 'placing'
  /** Заказ оформлен — карточка ведёт на него. В списке такое бывает мгновение. */
  | 'placed'
  | 'declined'
  | 'refunded';

export interface SbpPendingUi {
  kind: SbpPendingKind;
  title: string;
  pillLabel: string;
  variant: StatusVariant;
  /** Причина отказа или подсказка под заголовком. Пустая — строки нет. */
  detail: string;
}

const REFUND_HINT = 'Если деньги списались, они вернутся автоматически';

export function sbpPendingUi(payment: SbpPendingPayment, now: number = Date.now()): SbpPendingUi {
  const reason = (payment.failure_reason ?? '').trim();

  if (payment.refunded_at !== null) {
    return {
      kind: 'refunded',
      title: 'Деньги возвращены',
      pillLabel: 'Возврат',
      variant: 'neutral',
      detail: reason,
    };
  }
  if (payment.status === 'declined') {
    return {
      kind: 'declined',
      title: 'Оплата не прошла',
      pillLabel: 'Отказ',
      variant: 'danger',
      detail: reason.length > 0 ? reason : REFUND_HINT,
    };
  }
  if (payment.order_id !== null) {
    return {
      kind: 'placed',
      title: 'Заказ оформлен',
      pillLabel: 'Оформлен',
      variant: 'success',
      detail: '',
    };
  }
  if (payment.status === 'paid') {
    return {
      kind: 'placing',
      title: 'Оплата подтверждена, оформляем заказ',
      pillLabel: 'Оформляем',
      variant: 'info',
      detail: '',
    };
  }
  if (isExpired(payment, now)) {
    return {
      kind: 'expired',
      title: 'Банк не подтвердил оплату',
      pillLabel: 'Не подтверждён',
      variant: 'warning',
      detail: REFUND_HINT,
    };
  }
  return {
    kind: 'awaiting',
    title: 'Ожидает оплаты',
    pillLabel: 'Не оплачен',
    variant: 'warning',
    detail: '',
  };
}

/** Ждём ли по этому платежу ответа — ровно по таким блок и опрашивает сервер. */
export function sbpPendingUnsettled(payment: SbpPendingPayment, now: number = Date.now()): boolean {
  const { kind } = sbpPendingUi(payment, now);
  return kind === 'awaiting' || kind === 'placing';
}

function isExpired(payment: SbpPendingPayment, now: number): boolean {
  const expiresAt = Date.parse(payment.expires_at);
  return Number.isFinite(expiresAt) && now >= expiresAt + SBP_EXPIRY_GRACE_MS;
}

/** «6 800 ₽ · А123ВС77 · 25 л» — сумма, которую сверяют с банком, и чем её заслужили. */
export function sbpPendingSummary(payment: SbpPendingPayment): string {
  return [formatMoney(payment.amount), payment.plate?.trim() ?? '', formatLiters(payment.liters)]
    .filter((part) => part.length > 0)
    .join(' · ');
}

/** «Москва, Кремль · Сегодня, 14:32» — где и когда. Без адреса остаётся только когда. */
export function sbpPendingPlaceLabel(payment: SbpPendingPayment, now: Date = new Date()): string {
  return [payment.address_text?.trim() ?? '', historyDateTimeLabel(payment.created_at, now)]
    .filter((part) => part.length > 0)
    .join(' · ');
}
